/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  FormCancelButton,
  FormDialogActions,
  FormElement,
  FormInputsStack,
  MultiSelectorAutocomplete,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { AssigneeOption } from '../../pages/tickets/options';

export type TicketReassignFormData = {
  assignees: AssigneeOption[];
};

type TicketReassignModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: TicketReassignFormData) => Promise<void>;
  assignees: AssigneeOption[];
  currentAssigneeIds: string[];
};

const ticketReassignSchema = z.object({
  assignees: z.array(z.object({
    title: z.string(),
    value: z.string().min(1),
    status: z.string().optional(),
  })).min(1, 'At least one assignee is required'),
});

const TicketReassignModal = ({
  open,
  onClose,
  onSave,
  assignees,
  currentAssigneeIds,
}: TicketReassignModalProps) => {
  const initialAssignees = useMemo(
    () => currentAssigneeIds
      .map((id) => assignees.find((option) => option.value === id))
      .filter((option): option is AssigneeOption => !!option),
    [currentAssigneeIds, assignees],
  );

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    reset,
  } = useForm<TicketReassignFormData>({
    resolver: zodResolver(ticketReassignSchema),
    defaultValues: {
      assignees: initialAssignees,
    },
  });

  useEffect(() => {
    if (!open) return;
    reset({ assignees: initialAssignees });
  }, [open]); // intentionally reset only when modal opens/closes

  const onSubmit: SubmitHandler<TicketReassignFormData> = async (data) => {
    await onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Reassign Ticket</DialogTitle>
      <FormElement name="ticket-reassign-form" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ paddingTop: 0.5 }}>
          <FormInputsStack>
            <Typography variant="body" color="text.secondary">
              Assign a team member to take over further actions for this ticket:
            </Typography>

            <Controller
              control={control}
              name="assignees"
              render={({ field }) => (
                <MultiSelectorAutocomplete
                  {...field}
                  label="Assignee*"
                  name="ticket-reassign-assignees"
                  optionEntities={assignees.filter((option) => option.value !== '')}
                  valueFieldName="value"
                  labelFieldName="title"
                  onChange={(_: any, data: any[]) => {
                    field.onChange(data);
                  }}
                  error={!!errors.assignees}
                  helperText={(errors.assignees as { message?: string } | undefined)?.message}
                />
              )}
            />
          </FormInputsStack>
        </DialogContent>

        <FormDialogActions sx={{ mt: 0, border: 0, p: 4 }}>
          <FormCancelButton onClick={onClose} />
          <LoadingButton variant="contained" type="submit" {...getSubmitButtonProps(isSubmitting, isValid)}>
            Save
          </LoadingButton>
        </FormDialogActions>
      </FormElement>
    </Dialog>
  );
};

export default TicketReassignModal;
